import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function ClientsManagement({ admin }: { admin: any }) {
  const [clients, setClients] = useState<any[]>([]);
  const [filteredClients, setFilteredClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [clientJobs, setClientJobs] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState('newest');
  
  // Notification states
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showSpecialOfferModal, setShowSpecialOfferModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('info');
  const [sendingNotification, setSendingNotification] = useState(false);
  
  // Special offer states
  const [offerTitle, setOfferTitle] = useState('');
  const [offerDescription, setOfferDescription] = useState('');
  const [offerDiscount, setOfferDiscount] = useState('');
  const [offerCode, setOfferCode] = useState('');
  const [offerExpiry, setOfferExpiry] = useState('');
  const [offerTargetAudience, setOfferTargetAudience] = useState('all');
  const [selectedClientsForOffer, setSelectedClientsForOffer] = useState<string[]>([]);
  const [sendingOffer, setSendingOffer] = useState(false);

  useEffect(() => {
    loadClients();
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('client_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'users' },
        (payload) => {
          console.log('Client change detected:', payload);
          loadClients();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    filterClients();
  }, [searchTerm, statusFilter, sortBy, clients]);

  const loadClients = async () => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('role', 'client')
      .order('created_at', { ascending: false });

    if (data) {
      setClients(data);
    }
    setLoading(false);
  };

  const filterClients = () => {
    let filtered = [...clients];

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'name':
          return (a.full_name || '').localeCompare(b.full_name || '');
        default:
          return 0;
      }
    });

    setFilteredClients(filtered);
  };

  const updateClientStatus = async (clientId: string, newStatus: string) => {
    const { error } = await supabase
      .from('users')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', clientId);

    if (!error) {
      loadClients();
      if (selectedClient?.id === clientId) {
        setSelectedClient({ ...selectedClient, status: newStatus });
      }
      alert(`✅ Client status updated to ${newStatus}`);
    }
  };

  const viewClientDetails = async (client: any) => {
    setSelectedClient(client);
    setShowDetails(true);
    
    // Load client's jobs
    const { data: jobs } = await supabase
      .from('jobs')
      .select('*')
      .eq('client_id', client.id)
      .order('created_at', { ascending: false });
    
    if (jobs) {
      setClientJobs(jobs);
    }
  };

  const deleteClient = async (clientId: string) => {
    if (!confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      return;
    }

    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', clientId);

    if (!error) {
      alert('✅ Client deleted successfully');
      setShowDetails(false);
      loadClients();
    } else {
      alert('❌ Error deleting client');
    }
  };

  const sendNotificationToClient = async () => {
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      alert('Please fill in all notification fields');
      return;
    }

    setSendingNotification(true);
    try {
      const { error } = await supabase
        .from('client_notifications')
        .insert({
          client_id: selectedClient.id,
          title: notificationTitle,
          message: notificationMessage,
          type: notificationType,
          read: false,
          created_at: new Date().toISOString()
        });

      if (error) throw error;

      alert('✅ Notification sent successfully!');
      setShowNotificationModal(false);
      setNotificationTitle('');
      setNotificationMessage('');
      setNotificationType('info');
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('❌ Failed to send notification');
    } finally {
      setSendingNotification(false);
    }
  };

  const sendSpecialOffer = async () => {
    if (!offerTitle.trim() || !offerDescription.trim() || !offerDiscount.trim()) {
      alert('Please fill in all required offer fields');
      return;
    }

    setSendingOffer(true);
    try {
      let targetClients: string[] = [];
      
      if (offerTargetAudience === 'all') {
        targetClients = clients.filter(c => c.status === 'active').map(c => c.id);
      } else if (offerTargetAudience === 'selected') {
        targetClients = selectedClientsForOffer;
      } else if (offerTargetAudience === 'single' && selectedClient) {
        targetClients = [selectedClient.id];
      }

      if (targetClients.length === 0) {
        alert('No clients selected for this offer');
        setSendingOffer(false);
        return;
      }

      // Create notifications for all target clients
      const notifications = targetClients.map(clientId => ({
        client_id: clientId,
        title: `🎉 Special Offer: ${offerTitle}`,
        message: `${offerDescription}\n\n💰 Discount: ${offerDiscount}${offerCode ? `\n🏷️ Use code: ${offerCode}` : ''}${offerExpiry ? `\n⏰ Valid until: ${new Date(offerExpiry).toLocaleDateString()}` : ''}`,
        type: 'offer',
        read: false,
        created_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('client_notifications')
        .insert(notifications);

      if (error) throw error;

      alert(`✅ Special offer sent to ${targetClients.length} client(s)!`);
      setShowSpecialOfferModal(false);
      resetOfferForm();
    } catch (error) {
      console.error('Error sending special offer:', error);
      alert('❌ Failed to send special offer');
    } finally {
      setSendingOffer(false);
    }
  };

  const resetOfferForm = () => {
    setOfferTitle('');
    setOfferDescription('');
    setOfferDiscount('');
    setOfferCode('');
    setOfferExpiry('');
    setOfferTargetAudience('all');
    setSelectedClientsForOffer([]);
  };

  const toggleClientSelection = (clientId: string) => {
    setSelectedClientsForOffer(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const activeClientsCount = clients.filter(c => c.status === 'active').length;
  const suspendedClientsCount = clients.filter(c => c.status === 'suspended').length;
  const totalJobsCount = clients.reduce((sum, c) => sum + (c.total_jobs || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Client Management</h2>
          <p className="text-gray-600 mt-1">Manage and monitor all platform clients</p>
        </div>
        <button
          onClick={() => {
            setSelectedClient(null);
            setOfferTargetAudience('all');
            setShowSpecialOfferModal(true);
          }}
          className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all whitespace-nowrap cursor-pointer font-medium flex items-center gap-2 shadow-lg"
        >
          <i className="ri-gift-line text-lg w-5 h-5 flex items-center justify-center"></i>
          Send Special Offer
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/90">Total Clients</p>
              <p className="text-2xl font-bold text-white">{clients.length}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-user-line text-white text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/90">Active Clients</p>
              <p className="text-2xl font-bold text-white">{activeClientsCount}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-user-star-line text-white text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/90">Suspended</p>
              <p className="text-2xl font-bold text-white">{suspendedClientsCount}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-user-forbid-line text-white text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/90">Total Jobs</p>
              <p className="text-2xl font-bold text-white">{totalJobsCount}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-briefcase-line text-white text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Clients</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg w-5 h-5 flex items-center justify-center"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or phone..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name">Name (A-Z)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Clients List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-user-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg">No clients found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-semibold">
                          {(client.full_name || client.email || 'C')[0].toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {client.full_name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">ID: {client.id.slice(0, 8)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client.email}</div>
                      <div className="text-sm text-gray-500">{client.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client.city || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{client.postcode || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(client.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(client.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        client.status === 'active' ? 'bg-green-100 text-green-800' :
                        client.status === 'suspended' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {client.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => viewClientDetails(client)}
                          className="text-teal-600 hover:text-teal-900 font-medium cursor-pointer whitespace-nowrap"
                        >
                          View
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => {
                            setSelectedClient(client);
                            setShowNotificationModal(true);
                          }}
                          className="text-amber-600 hover:text-amber-900 font-medium cursor-pointer whitespace-nowrap"
                        >
                          Notify
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Client Details Modal */}
      {showDetails && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[95vh] overflow-y-auto my-8">
            <div className="sticky top-0 bg-gradient-to-r from-teal-50 to-cyan-50 p-6 border-b border-gray-200 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Client Details</h3>
                  <p className="text-gray-600 mt-1">
                    {selectedClient.email} - {selectedClient.status || 'active'}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-user-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Basic Information
                </h4>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="text-sm text-gray-600">Full Name</label>
                    <p className="font-medium text-gray-900">{selectedClient.full_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Email</label>
                    <p className="font-medium text-gray-900">{selectedClient.email}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Phone</label>
                    <p className="font-medium text-gray-900">{selectedClient.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Status</label>
                    <p className="font-medium text-gray-900">{selectedClient.status || 'active'}</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-map-pin-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Address Information
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-900">{selectedClient.address || 'N/A'}</p>
                  <p className="text-gray-600">{selectedClient.city} {selectedClient.postcode}</p>
                </div>
              </div>

              {/* Account Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Account Information
                </h4>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="text-sm text-gray-600">Account Created</label>
                    <p className="font-medium text-gray-900">
                      {new Date(selectedClient.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Last Updated</label>
                    <p className="font-medium text-gray-900">
                      {selectedClient.updated_at 
                        ? new Date(selectedClient.updated_at).toLocaleDateString()
                        : 'N/A'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Email Verified</label>
                    <p className="font-medium text-gray-900">
                      {selectedClient.email_verified ? '✅ Yes' : '❌ No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Client ID</label>
                    <p className="font-medium text-gray-900 text-xs">{selectedClient.id}</p>
                  </div>
                </div>
              </div>

              {/* Job History */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-briefcase-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  Job History ({clientJobs.length})
                </h4>
                {clientJobs.length === 0 ? (
                  <div className="bg-gray-50 p-8 rounded-lg text-center">
                    <i className="ri-briefcase-line text-4xl text-gray-300 mb-2"></i>
                    <p className="text-gray-500">No jobs yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clientJobs.map((job) => (
                      <div key={job.id} className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-gray-900">{job.service_type}</h5>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                            job.status === 'completed' ? 'bg-green-100 text-green-800' :
                            job.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            job.status === 'accepted' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {job.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{job.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>📅 {new Date(job.created_at).toLocaleDateString()}</span>
                          {job.total_amount && <span>💰 £{job.total_amount}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-gray-900 mb-4">Manage Client</h4>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => {
                      setShowDetails(false);
                      setShowNotificationModal(true);
                    }}
                    className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                  >
                    <i className="ri-notification-line text-xl w-5 h-5 flex items-center justify-center"></i>
                    Send Notification
                  </button>
                  <button
                    onClick={() => {
                      setShowDetails(false);
                      setOfferTargetAudience('single');
                      setShowSpecialOfferModal(true);
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                  >
                    <i className="ri-gift-line text-xl w-5 h-5 flex items-center justify-center"></i>
                    Send Special Offer
                  </button>
                  {selectedClient.status !== 'active' && (
                    <button
                      onClick={() => updateClientStatus(selectedClient.id, 'active')}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                    >
                      <i className="ri-check-line text-xl w-5 h-5 flex items-center justify-center"></i>
                      Activate Client
                    </button>
                  )}
                  {selectedClient.status !== 'suspended' && (
                    <button
                      onClick={() => {
                        if (confirm(`Suspend ${selectedClient.full_name || selectedClient.email}?`)) {
                          updateClientStatus(selectedClient.id, 'suspended');
                        }
                      }}
                      className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                    >
                      <i className="ri-pause-circle-line text-xl w-5 h-5 flex items-center justify-center"></i>
                      Suspend Client
                    </button>
                  )}
                  <button
                    onClick={() => deleteClient(selectedClient.id)}
                    className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer font-medium flex items-center gap-2"
                  >
                    <i className="ri-delete-bin-line text-xl w-5 h-5 flex items-center justify-center"></i>
                    Delete Client
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {showNotificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-notification-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Send Notification</h3>
                    <p className="text-white/80 text-sm">
                      To: {selectedClient?.full_name || selectedClient?.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowNotificationModal(false);
                    setNotificationTitle('');
                    setNotificationMessage('');
                  }}
                  className="text-white/80 hover:text-white cursor-pointer"
                >
                  <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Type
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'info', label: 'Info', icon: 'ri-information-line', color: 'bg-blue-100 text-blue-700 border-blue-300' },
                    { value: 'success', label: 'Success', icon: 'ri-check-line', color: 'bg-green-100 text-green-700 border-green-300' },
                    { value: 'warning', label: 'Warning', icon: 'ri-alert-line', color: 'bg-amber-100 text-amber-700 border-amber-300' },
                    { value: 'promo', label: 'Promo', icon: 'ri-gift-line', color: 'bg-purple-100 text-purple-700 border-purple-300' }
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => setNotificationType(type.value)}
                      className={`flex-1 px-3 py-2 rounded-lg border-2 flex items-center justify-center gap-2 cursor-pointer transition-all ${
                        notificationType === type.value 
                          ? type.color + ' border-current' 
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <i className={`${type.icon} w-4 h-4 flex items-center justify-center`}></i>
                      <span className="text-sm font-medium whitespace-nowrap">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  placeholder="Enter notification title..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value.slice(0, 500))}
                  placeholder="Enter your message..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                />
                <p className="text-xs text-gray-500 mt-1 text-right">{notificationMessage.length}/500</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowNotificationModal(false);
                    setNotificationTitle('');
                    setNotificationMessage('');
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={sendNotificationToClient}
                  disabled={sendingNotification || !notificationTitle.trim() || !notificationMessage.trim()}
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sendingNotification ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-5 h-5 flex items-center justify-center"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line w-5 h-5 flex items-center justify-center"></i>
                      Send Notification
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Special Offer Modal */}
      {showSpecialOfferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl my-8">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-gift-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Send Special Offer</h3>
                    <p className="text-white/80 text-sm">Create and send promotional offers to clients</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowSpecialOfferModal(false);
                    resetOfferForm();
                  }}
                  className="text-white/80 hover:text-white cursor-pointer"
                >
                  <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setOfferTargetAudience('all')}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      offerTargetAudience === 'all'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <i className="ri-group-line text-2xl text-amber-600 mb-2 w-8 h-8 flex items-center justify-center mx-auto"></i>
                    <p className="font-medium text-gray-900 text-sm">All Clients</p>
                    <p className="text-xs text-gray-500">{activeClientsCount} active</p>
                  </button>
                  <button
                    onClick={() => setOfferTargetAudience('selected')}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      offerTargetAudience === 'selected'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <i className="ri-user-add-line text-2xl text-amber-600 mb-2 w-8 h-8 flex items-center justify-center mx-auto"></i>
                    <p className="font-medium text-gray-900 text-sm">Select Clients</p>
                    <p className="text-xs text-gray-500">{selectedClientsForOffer.length} selected</p>
                  </button>
                  {selectedClient && (
                    <button
                      onClick={() => setOfferTargetAudience('single')}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        offerTargetAudience === 'single'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <i className="ri-user-line text-2xl text-amber-600 mb-2 w-8 h-8 flex items-center justify-center mx-auto"></i>
                      <p className="font-medium text-gray-900 text-sm">Single Client</p>
                      <p className="text-xs text-gray-500 truncate">{selectedClient.full_name || selectedClient.email}</p>
                    </button>
                  )}
                </div>
              </div>

              {/* Client Selection (if selected audience) */}
              {offerTargetAudience === 'selected' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Clients ({selectedClientsForOffer.length} selected)
                  </label>
                  <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {clients.filter(c => c.status === 'active').map(client => (
                      <label
                        key={client.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={selectedClientsForOffer.includes(client.id)}
                          onChange={() => toggleClientSelection(client.id)}
                          className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500 cursor-pointer"
                        />
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-sm font-semibold">
                          {(client.full_name || client.email || 'C')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{client.full_name || 'N/A'}</p>
                          <p className="text-xs text-gray-500 truncate">{client.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Offer Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Offer Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={offerTitle}
                    onChange={(e) => setOfferTitle(e.target.value)}
                    placeholder="e.g., Summer Sale, New Customer Discount..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Offer Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={offerDescription}
                    onChange={(e) => setOfferDescription(e.target.value.slice(0, 500))}
                    placeholder="Describe your special offer..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-right">{offerDescription.length}/500</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={offerDiscount}
                    onChange={(e) => setOfferDiscount(e.target.value)}
                    placeholder="e.g., 20% off, £10 off..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Promo Code (Optional)
                  </label>
                  <input
                    type="text"
                    value={offerCode}
                    onChange={(e) => setOfferCode(e.target.value.toUpperCase())}
                    placeholder="e.g., SUMMER20"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm uppercase"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={offerExpiry}
                    onChange={(e) => setOfferExpiry(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm cursor-pointer"
                  />
                </div>
              </div>

              {/* Preview */}
              {offerTitle && offerDescription && offerDiscount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-gift-line text-white text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">🎉 Special Offer: {offerTitle}</h4>
                        <p className="text-sm text-gray-700 mt-1">{offerDescription}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium">
                            💰 {offerDiscount}
                          </span>
                          {offerCode && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium">
                              🏷️ Code: {offerCode}
                            </span>
                          )}
                          {offerExpiry && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium">
                              ⏰ Until: {new Date(offerExpiry).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowSpecialOfferModal(false);
                    resetOfferForm();
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={sendSpecialOffer}
                  disabled={sendingOffer || !offerTitle.trim() || !offerDescription.trim() || !offerDiscount.trim() || (offerTargetAudience === 'selected' && selectedClientsForOffer.length === 0)}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all whitespace-nowrap cursor-pointer font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sendingOffer ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-5 h-5 flex items-center justify-center"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line w-5 h-5 flex items-center justify-center"></i>
                      Send Offer ({offerTargetAudience === 'all' ? activeClientsCount : offerTargetAudience === 'selected' ? selectedClientsForOffer.length : 1} clients)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
